import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { RecordPageShell } from '~/components/layout';
import {
  Alert,
  Card,
  CardBody,
  CardFooter,
  CardSection,
  FormActions,
  Skeleton,
} from '~/components/ui';
import { FormField, TextField, Select } from '~/components/form';
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard';
import { routes } from '~/app/routes';
import type { CustomerAgent } from '~/modules/query/customers';
import {
  useEnableSocialMutation,
  useGetCustomersQuery,
  useLazyGetCustomerAgentsQuery,
  useSendAgentMutation,
  useSetAgentBayAccountIdMutation,
} from '~/modules/query/customers';
import { queryErrorMessage } from '~/modules/query/base';

/** The customer fields the wizard reads. */
export interface EnableSocialCustomer {
  companyid: string | number;
  company_name?: string;
  bay_parent_account_id?: string | number | null;
}

const STEP_TITLES = [
  'Parent account ID',
  'Parent account user',
  'Enable Social',
  'Set user BAY account',
  'Sync user with Social',
  'Done',
];

// The primary button names what pressing it does, so an action step never
// reads as a harmless "Next".
const STEP_ACTION_LABEL: Record<number, string> = {
  1: 'Next',
  2: 'Next',
  3: 'Enable Social',
  4: 'Set BAY account',
  5: 'Sync user with Social',
};

const StepInfo = ({
  title,
  tone = 'info',
  children,
}: {
  title: string;
  tone?: 'info' | 'success';
  children: ReactNode;
}) => (
  <Alert variant={tone}>
    <p className="mb-1 text-sm font-medium text-gray-800 dark:text-white/90">{title}</p>
    <div className="text-sm text-gray-800 dark:text-white/90">{children}</div>
  </Alert>
);

const LEAVE_MESSAGE =
  'Enabling Social is not finished. Steps already run stay applied; leaving now skips the rest.';

// Multi-step wizard that does every part of enabling Social for one customer.
const EnableSocialWizard = ({ customer }: { customer: EnableSocialCustomer }) => {
  const navigate = useNavigate();
  const initialParentId = customer.bay_parent_account_id
    ? String(customer.bay_parent_account_id)
    : '';
  const [step, setStep] = useState(1);
  const [parentId, setParentId] = useState(initialParentId);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [agents, setAgents] = useState<CustomerAgent[]>();
  const [loadAgents] = useLazyGetCustomerAgentsQuery();
  const [enableSocial, enableSocialState] = useEnableSocialMutation();
  const [setAgentBayAccountId, setAgentBayAccountIdState] = useSetAgentBayAccountIdMutation();
  const [sendAgent, sendAgentState] = useSendAgentMutation();
  const busy =
    enableSocialState.isLoading || setAgentBayAccountIdState.isLoading || sendAgentState.isLoading;

  const companyName = customer.company_name ?? customer.companyid;
  const guard = useUnsavedGuard(
    step < STEP_TITLES.length && (step > 1 || parentId !== initialParentId),
    LEAVE_MESSAGE,
  );
  const progress = Math.round((step / STEP_TITLES.length) * 100);

  useEffect(() => {
    let cancelled = false;
    setAgents(undefined);

    loadAgents(customer.companyid).then((result) => {
      if (cancelled) return;
      if (result.error) {
        toast.error(
          queryErrorMessage(result.error) ?? 'The agents could not be loaded. Try again.',
        );
        setAgents([]);
        return;
      }
      setAgents(result.data ?? []);
    });

    return () => {
      cancelled = true;
    };
  }, [customer.companyid, loadAgents]);

  // Eligible "parent account user":
  //   1. the agent already carrying this parent account id (bay_account_id = parent id), if any —
  //      then that is the only one we offer; else
  //   2. agents with no bay_account_id yet.
  const eligibleAgents = useMemo(() => {
    const list = agents ?? [];
    const parentAccountId = Number(parentId);

    if (Number.isInteger(parentAccountId) && parentAccountId > 0) {
      const alreadyParent = list.filter(
        (agent) => Number(agent.bay_account_id) === parentAccountId,
      );

      if (alreadyParent.length) {
        return alreadyParent;
      }
    }

    return list.filter((agent) => !agent.bay_account_id || Number(agent.bay_account_id) === 0);
  }, [agents, parentId]);

  const agentOptions = useMemo(
    () =>
      eligibleAgents.map((agent) => ({
        value: agent.publicid,
        label: `${agent.firstname} ${agent.lastname} (${agent.publicid})`,
      })),
    [eligibleAgents],
  );

  // Resolve the picked user's display name from the loaded agents by id.
  const selectedName = useMemo(() => {
    if (!selectedAgentId) {
      return '';
    }

    const agent = (agents ?? []).find((a) => a.publicid === selectedAgentId);

    return agent ? `${agent.firstname} ${agent.lastname}` : '';
  }, [agents, selectedAgentId]);

  // When there's exactly one eligible user, it's the answer — preselect it.
  useEffect(() => {
    if (agentOptions.length === 1) {
      setSelectedAgentId(agentOptions[0].value);
    }
  }, [agentOptions]);

  // Input validation is reflected by disabling the primary button, not a toast.
  const numericParentId = /^\d+$/.test(parentId.trim()) && Number(parentId) > 0;
  const canProceed = step === 1 ? numericParentId : step === 2 ? Boolean(selectedAgentId) : true;

  const next = async () => {
    if (!canProceed) {
      return;
    }

    if (step === 1) {
      setStep(2);
      return;
    }
    if (step === 2) {
      setStep(3);
      return;
    }

    try {
      if (step === 3) {
        await enableSocial({
          companyID: customer.companyid,
          BAYParentAccountID: Number(parentId),
        }).unwrap();
      } else if (step === 4) {
        await setAgentBayAccountId({
          companyID: customer.companyid,
          publicId: selectedAgentId,
          BAYAccountID: Number(parentId),
        }).unwrap();
      } else if (step === 5) {
        await sendAgent({ companyID: customer.companyid, publicId: selectedAgentId }).unwrap();
      }
      setStep((s) => s + 1);
    } catch (err) {
      console.error(err);
      toast.error(queryErrorMessage(err) ?? 'This step could not be completed. Try again.');
    }
  };

  return (
    <RecordPageShell
      title={`Enable Social for ${companyName}`}
      description="Turns on Social for this customer and syncs its parent account user."
      breadcrumbs={[
        { label: 'Customers', href: routes.customers },
        { label: `Enable Social for ${companyName}` },
      ]}
      guard={guard}
      testId="enable-social"
    >
      <Card>
        <CardBody className="space-y-6">
          <CardSection
            title={STEP_TITLES[step - 1]}
            description={`Step ${step} of ${STEP_TITLES.length}`}
            actions={<span className="text-sm text-gray-500 dark:text-gray-400">{progress}%</span>}
          >
            <div
              className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800"
              role="progressbar"
              aria-label="Wizard progress"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={progress}
            >
              <div
                className="h-full w-(--w) rounded-full bg-brand-500 transition-all duration-300 motion-reduce:transition-none"
                style={{ '--w': `${progress}%` } as React.CSSProperties}
              />
            </div>
          </CardSection>

          {step === 1 && (
            <TextField
              id="enable-social-parent-id"
              testId="enable-social-parent-id-input"
              label="Parent account ID"
              required
              helperText="The Social parent account this customer's agents hang off."
              value={parentId}
              onChange={setParentId}
              placeholder="e.g. 12345"
            />
          )}

          {step === 2 && (
            <FormField
              id="enable-social-parent-user"
              label="Parent account user"
              required
              helperText={
                !agents
                  ? 'Loading agents…'
                  : !agentOptions.length
                    ? 'No eligible users.'
                    : Number(eligibleAgents[0]?.bay_account_id) === Number(parentId) &&
                        eligibleAgents.length
                      ? 'This user already carries the parent account id.'
                      : 'Only agents that do not already have a BAY account id are listed.'
              }
            >
              {agents ? (
                <Select
                  id="enable-social-parent-user"
                  testId="enable-social-parent-user-select"
                  placeholder="Search agents…"
                  placeholderSelectable
                  options={agentOptions}
                  value={selectedAgentId}
                  onChange={setSelectedAgentId}
                />
              ) : (
                <Skeleton className="h-11" />
              )}
            </FormField>
          )}

          {step === 3 && (
            <StepInfo title="Enabling Social will do the following things:">
              <ol className="list-decimal space-y-1 pl-5">
                <li>
                  Turns on Social for <strong>{companyName}</strong>
                </li>
                <li>
                  Sets <strong>{parentId}</strong> as {companyName}'s Parent Account
                </li>
                <li>
                  Sets <strong>{selectedName}</strong> as {companyName}'s Parent Account
                </li>
                <li>
                  Enables Social Login for <strong>{selectedName}</strong>
                </li>
                <li>
                  Syncs <strong>{selectedName}</strong> with Social
                </li>
              </ol>
            </StepInfo>
          )}

          {step === 4 && (
            <StepInfo title="Set user BAY account">
              Sets <strong>{selectedName}</strong>'s <code>bay_account_id</code> to{' '}
              <strong>{parentId}</strong> and enables Social login for them (
              <code>smc_login = 'T'</code>).
            </StepInfo>
          )}

          {step === 5 && (
            <StepInfo title="Sync user with Social">
              Calls office's <code>sync.cfc?method=sendAgent</code> for{' '}
              <strong>{selectedName}</strong>, pushing the user to Social.
            </StepInfo>
          )}

          {step === 6 && (
            <StepInfo title="Done" tone="success">
              Social is enabled for <strong>{customer.companyid}</strong> and{' '}
              <strong>{selectedName}</strong> is synced. Sync the rest of the agents if needed.
            </StepInfo>
          )}
        </CardBody>
        <CardFooter>
          {step < 6 ? (
            <FormActions
              discardLabel="Back"
              discardDisabled={step === 1}
              onDiscard={() => setStep((s) => Math.max(1, s - 1))}
              discardTestId="enable-social-back-button"
              saveLabel={STEP_ACTION_LABEL[step]}
              busyLabel="Working…"
              busy={busy}
              disabled={!canProceed}
              disabledReason={
                step === 1 ? 'Enter a numeric parent account ID' : 'Choose a parent account user'
              }
              onSave={next}
              saveTestId="enable-social-next-button"
            />
          ) : (
            <FormActions
              discardLabel="Close"
              discardHref={routes.customers}
              saveLabel="Sync agents"
              onSave={() =>
                navigate(
                  `${routes.customers}?sync=${encodeURIComponent(String(customer.companyid))}`,
                )
              }
              saveTestId="enable-social-sync-agents-button"
            />
          )}
        </CardFooter>
      </Card>
    </RecordPageShell>
  );
};

/** `/customers/:id/enable-social`: finds the customer, then runs the wizard on the page. */
export const EnableSocialPage = () => {
  const { id = '' } = useParams<{ id: string }>();
  const customers = useGetCustomersQuery();
  const customer = (customers.data as EnableSocialCustomer[] | undefined)?.find(
    (row) => String(row.companyid) === id,
  );

  if (customer) return <EnableSocialWizard customer={customer} />;
  return (
    <RecordPageShell
      title="Enable Social"
      breadcrumbs={[{ label: 'Customers', href: routes.customers }, { label: 'Enable Social' }]}
      loading={customers.isLoading}
      notFound={!customers.isLoading && !customers.isError}
      notFoundTitle="Customer not found"
      backTo={routes.customers}
      backLabel="Back to customers"
      error={
        customers.error
          ? { what: 'the customer', message: queryErrorMessage(customers.error) }
          : null
      }
      onRetry={() => customers.refetch()}
      testId="enable-social"
    />
  );
};
